import { useState, useEffect } from 'react';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Eye, Heart, MessageCircle, Share2, Bookmark, 
  Link2, UserPlus, Users, TrendingUp, Download,
  Loader2, BarChart3, Clock
} from 'lucide-react';


// Metric Card Component (moved outside to prevent re-creation on each render)
function MetricCard({ icon: Icon, label, value, subValue, color = 'text-primary' }) {
  return (
    <Card className="p-4 bg-muted/30 border-border">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-bold">{value?.toLocaleString() || 0}</p>
          {subValue && (
            <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
          )}
        </div>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </Card>
  );
}

export function PostInsightsModal({ open, onOpenChange, postId, postPreview }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/posts/${postId}/insights?time_range=${timeRange}`,
        { headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        setInsights(data);
      } else {
        toast.error('Failed to load insights');
      }
    } catch (err) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && postId) {
      fetchInsights();
    }
  }, [open, postId, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    try {
      const res = await fetch(
        `${API}/api/posts/${postId}/insights/export`,
        { headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        
        // Create and download CSV
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success('Insights exported');
      }
    } catch (err) {
      toast.error('Failed to export insights');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Post Insights
          </DialogTitle>
          <DialogDescription>
            Analytics and performance metrics for your post
          </DialogDescription>
        </DialogHeader>

        {/* Time Range Tabs */}
        <Tabs value={timeRange} onValueChange={setTimeRange} className="mb-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="24h">Last 24h</TabsTrigger>
            <TabsTrigger value="7d">Last 7 days</TabsTrigger>
            <TabsTrigger value="30d">Last 30 days</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : insights ? (
          <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <MetricCard 
                icon={Eye} 
                label="Total Views" 
                value={insights.view_count}
                subValue={`${insights.unique_view_count} unique`}
              />
              <MetricCard 
                icon={Heart} 
                label="Likes" 
                value={insights.like_count}
                color="text-red-400"
              />
              <MetricCard 
                icon={MessageCircle} 
                label="Comments" 
                value={insights.comment_count}
                color="text-blue-400"
              />
              <MetricCard 
                icon={Share2} 
                label="Shares" 
                value={insights.share_count}
                color="text-green-400"
              />
              <MetricCard 
                icon={Bookmark} 
                label="Saves" 
                value={insights.save_count}
                color="text-purple-400"
              />
              <MetricCard 
                icon={Link2} 
                label="Link Clicks" 
                value={insights.link_click_count}
                color="text-orange-400"
              />
            </div>

            {/* Engagement Rate */}
            <Card className="p-4 bg-muted/30 border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Engagement Rate</span>
                <Badge variant="secondary">
                  {insights.engagement_rate}%
                </Badge>
              </div>
              <Progress value={Math.min(insights.engagement_rate, 100)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                (Likes + Comments + Shares + Saves) / Views
              </p>
            </Card>

            {/* Profile Visits & Follower Gains */}
            <div className="grid grid-cols-2 gap-3">
              <MetricCard 
                icon={Users} 
                label="Profile Visits" 
                value={insights.profile_visit_count}
                subValue="From this post"
                color="text-cyan-400"
              />
              <MetricCard 
                icon={UserPlus} 
                label="Follower Gains" 
                value={insights.follower_gain_count}
                subValue="Attributed to this post"
                color="text-emerald-400"
              />
            </div>

            {/* Top Referrers */}
            {insights.top_referrers?.length > 0 && (
              <Card className="p-4 bg-muted/30 border-border">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Top Referrers
                </h4>
                <div className="space-y-2">
                  {insights.top_referrers.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{ref.source.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground">{ref.count} views</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Daily Views Chart (Simple Bar Representation) */}
            {Object.keys(insights.daily_views || {}).length > 0 && (
              <Card className="p-4 bg-muted/30 border-border">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Views Over Time
                </h4>
                <div className="space-y-2">
                  {Object.entries(insights.daily_views)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .slice(-7)
                    .map(([date, views]) => {
                      const maxViews = Math.max(...Object.values(insights.daily_views));
                      const percent = maxViews > 0 ? (views / maxViews) * 100 : 0;
                      
                      return (
                        <div key={date} className="flex items-center gap-3 text-sm">
                          <span className="w-20 text-muted-foreground text-xs">
                            {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <div className="flex-1 h-4 bg-muted/50 rounded overflow-hidden">
                            <div 
                              className="h-full bg-primary/60 rounded"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <span className="w-12 text-right">{views}</span>
                        </div>
                      );
                    })}
                </div>
              </Card>
            )}

            {/* Export Button */}
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleExport}
            >
              <Download className="w-4 h-4 mr-2" />
              Export to CSV
            </Button>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No insights data available yet
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PostInsightsModal;
